import { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface BackofficePageProps {
  title: string;
  description?: string;
  actions?: ReactNode;
  /**
   * Drop the reading-width cap, for pages whose content is a wide grid rather
   * than prose or a table. The side margins narrow but stay wide enough to
   * park the cursor in, clear of anything that captures the wheel.
   */
  wide?: boolean;
  children: ReactNode;
}

export default function BackofficePage({
  title,
  description,
  actions,
  wide = false,
  children,
}: BackofficePageProps) {
  return (
    <div
      className={`mx-auto py-8 ${
        // px-20 is 80px. Keep to Tailwind's scale here (…14, 16, 20, 24): an
        // off-scale class like px-21 does not exist and silently renders with
        // no padding at all.
        wide ? 'max-w-none px-20' : 'max-w-[1400px] px-8'
      }`}
    >
      <div className="flex items-start justify-between border-b border-[#1e242a] pb-5 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#6b7480] mb-1">
            Backoffice
          </div>
          <h1 className="text-xl font-semibold text-white">{title}</h1>
          {description && (
            <p className="text-sm text-[#a8b0bb] mt-1">{description}</p>
          )}
        </div>
        {actions && <div>{actions}</div>}
      </div>
      {children}
    </div>
  );
}

export function DataCard({ children }: { children: ReactNode }) {
  return (
    <div className="bg-[#161c22] border border-[#1e242a] rounded-lg overflow-hidden">
      {children}
    </div>
  );
}

export function DataTable({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">{children}</table>
    </div>
  );
}

export function TH({
  children,
  align = 'left',
}: {
  children: ReactNode;
  align?: 'left' | 'center' | 'right';
}) {
  return (
    <th
      className={`px-4 py-3 text-[10px] uppercase tracking-wider font-medium text-[#6b7480] border-b border-[#1e242a] text-${align}`}
    >
      {children}
    </th>
  );
}

export function TR({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <tr
      className={cn(
        'border-b border-[#1e242a] last:border-b-0 hover:bg-[#1a2126] transition',
        className,
      )}
    >
      {children}
    </tr>
  );
}

export function TD({
  children,
  align = 'left',
  mono = false,
  className = '',
}: {
  children: ReactNode;
  align?: 'left' | 'center' | 'right';
  mono?: boolean;
  className?: string;
}) {
  return (
    <td
      className={`px-4 py-3 text-${align} ${
        mono ? 'font-mono text-xs' : ''
      } text-[#d8dde2] ${className}`}
    >
      {children}
    </td>
  );
}
